import React, { ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useInfiniteQuery } from '@tanstack/react-query'
import { ITicket } from '../../../../../interfaces/models/ticket.interfaces'
import { cn } from '../../../../../helpers/utils'
import { theme } from '../../../../../theme/theme'
import { formatRelative } from '../../../../../helpers/date'
import { NotificationBadge } from '../../../../../components/notifications/NotificationBadge/NotificationBadge'
import { reverseUrl } from '../../../../../navigation/reverseUrl'
import { TicketBadge } from '../../../../../components/ticket/TicketBadge'
import { TicketVariantBadge } from '../../../../../components/ticket/TicketVariantBadge'
import { useAppTranslation } from '../../../../../hooks/useAppTranslation'
import { useCurrentSociety } from '../../../../../hooks/useCurrentSociety'
import {
  TicketFilterResident,
  infiniteTicketQueries,
} from '../../../../../api/ticket'
import { ErrorView } from '../../../../error/ErrorView/ErrorView'
import { IllustrationChoices } from '../../../../../components/common/Illustration'
import { InfiniteScroll } from '../../../../../components/common/InfiniteScroll'

const TicketMessage = ({
  ticketMessage,
}: {
  ticketMessage?: ITicket['lastMessage']
}): ReactNode => {
  const { translate } = useAppTranslation()
  const getSender = (): ReactNode => {
    if (ticketMessage?.type === 'me') {
      return translate('ticketListView.sender.me')
    }
    return translate('ticketListView.sender.board')
  }

  const getMessage = (): string => {
    if (ticketMessage?.message) return ticketMessage.message
    if (ticketMessage?.mediaIds.length) {
      return translate('ticketListView.message.sentImage')
    }
    if (ticketMessage?.documentIds.length) {
      return translate('ticketListView.message.sentFile')
    }
    return ''
  }
  return (
    <span>
      {getSender()}: {getMessage()}
    </span>
  )
}

export const TicketListItem = ({
  ticket,
  active,
  className,
}: {
  ticket: ITicket
  active?: boolean
  className?: string
}): ReactNode => {
  return (
    <Link
      to={reverseUrl('society:ticket-detail', {
        ticketId: ticket.id,
        id: ticket.societyId,
      })}
      style={theme.textVariants.base}
      className={cn(
        ' hover:bg-gray-light rounded-md px-2 py-3 flex flex-col',
        active ? 'bg-gray-light' : '',
        ticket.unreadMessages > 0
          ? 'bg-almostBrandGreen/10 border-brandGreen'
          : '',
        className
      )}
    >
      <div className="flex justify-between">
        <span
          style={theme.textVariants.caption}
          className="whitespace-nowrap text-neutral-50"
        >
          #{ticket.ticketNumber}
        </span>
        <span
          style={theme.textVariants.caption}
          className="whitespace-nowrap text-neutral-50"
        >
          {formatRelative(ticket.lastMessage?.createdAt || ticket.createdAt)}
        </span>
      </div>
      <p style={theme.textVariants.baseBold} className="line-clamp-1 min-w-0">
        {ticket.title}
      </p>
      <div className="line-clamp-1">
        <TicketMessage ticketMessage={ticket.lastMessage} />
      </div>
      <div className="flex flex-col  justify-between min-w-fit mt-2">
        <div className="flex gap-2 justify-end items-center">
          <TicketVariantBadge variant={ticket.variant} />
          <TicketBadge status={ticket.status} />
          {ticket.unreadMessages > 0 && (
            <NotificationBadge show absoluteOffset={false} />
          )}
        </div>
      </div>
    </Link>
  )
}

const filter = (variant: 'all' | 'open'): TicketFilterResident => {
  if (variant === 'all') {
    return {
      status: ['open', 'closed'],
    }
  }
  if (variant === 'open') {
    return {
      status: ['open'],
    }
  }
  return {
    status: ['open'],
  }
}
export const TicketListView = ({
  variant,
}: {
  variant: 'all' | 'open'
}): ReactNode => {
  const { ticketId } = useParams()
  const { society } = useCurrentSociety()
  const { translate } = useAppTranslation()
  const { data, isFetching, fetchNextPage, isLoading } = useInfiniteQuery(
    infiniteTicketQueries.tickets({
      societyId: society?._id,
      filter: filter(variant),
    })
  )
  const tickets = data?.pages.flatMap((page) => page.items || [])
  const isEmpty = !tickets?.length && !isLoading

  if (isEmpty) {
    return (
      <ErrorView
        title={translate('ticketListView.empty.title')}
        subtitle={translate('ticketListView.empty.subtitle', {
          context: variant,
        })}
        illustration={IllustrationChoices.EMPTY}
        className="mt-14"
      />
    )
  }

  return (
    <InfiniteScroll
      className="mt-4"
      onEndReached={() => fetchNextPage({ cancelRefetch: false })}
      isFetching={isFetching}
    >
      {tickets?.map((ticket) => {
        return (
          <TicketListItem
            active={ticket.id === ticketId}
            key={ticket.id}
            ticket={ticket}
          />
        )
      })}
    </InfiniteScroll>
  )
}
